import { useEffect, useId, useRef, useState } from 'react';
import {
  NETWORK_LIFECYCLE_LABELS_UK,
  NETWORK_LIFECYCLE_ORDER,
  normalizeNetworkLifecycle,
  type NetworkLifecycle,
} from '@ukrop-nm/shared';

const FIELD =
  'focus-ring min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200';

const AREA =
  'focus-ring min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200';

export type SaveProjectValues = {
  name: string;
  lifecycle: NetworkLifecycle;
  purposeGoal: string;
  purposeAudience: string;
  purposeScope: string;
};

export function SaveProjectModal({
  open,
  initial,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  initial: SaveProjectValues;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (values: SaveProjectValues) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [nameError, setNameError] = useState('');
  const titleId = useId();
  const descId = useId();
  const nameId = useId();
  const nameErrorId = useId();
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(initial);
    setNameError('');
    const timer = window.setTimeout(() => nameRef.current?.focus(), 20);
    return () => window.clearTimeout(timer);
    // Snapshot fields when the dialog opens — not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const submit = () => {
    if (busy) return;
    const name = draft.name.trim();
    if (!name) {
      setNameError('Вкажіть назву проєкту / схеми.');
      nameRef.current?.focus();
      return;
    }
    onConfirm({
      ...draft,
      name,
      lifecycle: normalizeNetworkLifecycle(draft.lifecycle),
    });
  };

  return (
    <div
      className="modal-root"
      role="presentation"
      onClick={onCancel}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onCancel();
      }}
    >
      <div className="modal-backdrop" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="modal-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id={titleId}>Параметри збереження</h3>
          <button type="button" className="btn-sm" aria-label="Закрити" onClick={onCancel}>
            ✕
          </button>
        </div>
        <div className="modal-body grid gap-3">
          <p id={descId} className="text-sm text-gray-600 dark:text-gray-300">
            Назва, статус і опис проєкту. Той самий опис потім підставляється в PDF і в пакет генерації.
          </p>
          {nameError ? (
            <div className="rounded-lg bg-error-50 px-3 py-2 text-sm text-error-500 dark:bg-error-500/15 dark:text-error-400" role="alert">
              <a href={`#${nameId}`} className="underline underline-offset-2">
                {nameError}
              </a>
            </div>
          ) : null}
          <label className="grid gap-1 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Назва проєкту / схеми</span>
            <input
              ref={nameRef}
              id={nameId}
              className={FIELD}
              value={draft.name}
              aria-invalid={Boolean(nameError)}
              aria-describedby={nameError ? nameErrorId : undefined}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, name: event.target.value }));
                if (nameError) setNameError('');
              }}
            />
            {nameError ? (
              <span id={nameErrorId} className="text-sm text-error-500 dark:text-error-400">
                {nameError}
              </span>
            ) : null}
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Статус проєкту</span>
            <select
              className={FIELD}
              value={normalizeNetworkLifecycle(draft.lifecycle)}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, lifecycle: event.target.value as NetworkLifecycle }))
              }
            >
              {NETWORK_LIFECYCLE_ORDER.map((id) => (
                <option key={id} value={id}>
                  {NETWORK_LIFECYCLE_LABELS_UK[id]}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Мета документа</span>
            <textarea
              className={AREA}
              rows={3}
              value={draft.purposeGoal}
              onChange={(event) => setDraft((prev) => ({ ...prev, purposeGoal: event.target.value }))}
              placeholder="Наприклад: документація LAN/WAN перед впровадженням моніторингу"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Для кого документ</span>
            <textarea
              className={AREA}
              rows={2}
              value={draft.purposeAudience}
              onChange={(event) => setDraft((prev) => ({ ...prev, purposeAudience: event.target.value }))}
              placeholder="Наприклад: адміністратори мережі ПУ, підрядник звʼязку"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Межі схеми</span>
            <textarea
              className={AREA}
              rows={2}
              value={draft.purposeScope}
              onChange={(event) => setDraft((prev) => ({ ...prev, purposeScope: event.target.value }))}
              placeholder="Наприклад: логічна схема зовнішніх каналів і ядра; без фізичних трас СКС"
            />
          </label>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Скасувати
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Збереження…' : 'Зберегти в хмару'}
          </button>
        </div>
      </div>
    </div>
  );
}
